import { useEffect, useRef } from "react";
import { getInsights } from "../../../core/services/analyze.service";
import {
  getPersistedMessageId,
  hasDisplayableInsights,
} from "../../../core/utils/analyzeInsights";

const isAssistantRole = (role) => {
  const r = String(role ?? "").toLowerCase();
  return r === "ai" || r === "assistant";
};

const isTemporaryClientMessageId = (id) =>
  !id || String(id).trim().startsWith("msg-");


export function useLoadChatInsights(messages, setMessages) {
  const inFlightRef = useRef(new Set());

  useEffect(() => {
    const source = Array.isArray(messages) ? messages : [];
    let cancelled = false;

    const run = async () => {
      for (const msg of source) {
        if (cancelled) break;
        if (!isAssistantRole(msg.role)) continue;

        const meta = msg.meta;
        const text = String(msg.text ?? msg.displayContent ?? msg.content ?? "");
        const hasViz =
          (Array.isArray(meta?.charts) && meta.charts.length > 0) ||
          (Array.isArray(meta?.data) && meta.data.length > 0);
        const looksLikeUpload =
          /file\s+".+"\s+uploaded/i.test(text) ||
          !!meta?.fileName ||
          !!meta?.schema ||
          !!meta?.userSessionFileId;
        if (!hasViz && !looksLikeUpload) continue;

        const hasFaqs =
          Array.isArray(meta?.faqs) && meta.faqs.some((q) => String(q ?? "").trim());
        if (hasDisplayableInsights(meta?.insights) && hasFaqs) continue;

        const mid = getPersistedMessageId(msg);
        if (isTemporaryClientMessageId(mid)) continue;

        const key = String(mid);
        if (inFlightRef.current.has(key)) continue;
        inFlightRef.current.add(key);

        try {
          const displayMeta = await getInsights(mid);
          if (cancelled) continue;
          if (!displayMeta?.insights && !(displayMeta?.faqs || []).length) continue;

          setMessages((prev) => {
            if (!Array.isArray(prev)) return prev;
            return prev.map((m) => {
              if (String(getPersistedMessageId(m) ?? "") !== key) return m;
              const currentMeta = m.meta || {};
              const nextInsights = hasDisplayableInsights(currentMeta.insights)
                ? currentMeta.insights
                : displayMeta.insights;
              const nextFaqs =
                Array.isArray(currentMeta.faqs) && currentMeta.faqs.length
                  ? currentMeta.faqs
                  : displayMeta.faqs;
              return {
                ...m,
                meta: {
                  ...currentMeta,
                  insights: nextInsights,
                  faqs: nextFaqs,
                  ...(displayMeta.parquetFileName
                    ? { fileName: currentMeta.fileName || displayMeta.parquetFileName }
                    : {}),
                },
              };
            });
          });
        } catch {
        } finally {
          inFlightRef.current.delete(key);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [messages, setMessages]);
}
